import { useReducer, useCallback } from "react";
import { userApi } from "./useUserApi";
import { userReducer, USER_ACTIONS } from "./useUserReducer";
import { USER_ERROR_MESSAGES } from "../constants";

export const useUserManagement = () => {
  const [state, dispatch] = useReducer(userReducer, {
    users: [],
    loading: false,
    error: null,
    selectedUser: null,
  });

  // Clear error
  const clearError = useCallback(() => {
    dispatch({ type: USER_ACTIONS.CLEAR_ERROR });
  }, []);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      const users = await userApi.getAllUsers();
      dispatch({ type: USER_ACTIONS.SET_USERS, payload: users });
    } catch (error) {
      dispatch({
        type: USER_ACTIONS.SET_ERROR,
        payload: error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.FETCH_USERS,
      });
    }
  }, []);

  // Create user
  const createUser = useCallback(async userData => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      const newUser = await userApi.createUser(userData);
      dispatch({ type: USER_ACTIONS.ADD_USER, payload: newUser });
      return newUser;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.CREATE_USER;
      dispatch({ type: USER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Update user
  const updateUser = useCallback(async (email, userData) => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      const updatedUser = await userApi.updateUser(email, userData);
      dispatch({ type: USER_ACTIONS.UPDATE_USER, payload: updatedUser });
      return updatedUser;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.UPDATE_USER;
      dispatch({ type: USER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Delete user
  const deleteUser = useCallback(async email => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      await userApi.deleteUser(email);
      dispatch({ type: USER_ACTIONS.DELETE_USER, payload: email });
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.DELETE_USER;
      dispatch({ type: USER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Get user by email
  const getUserByEmail = useCallback(async email => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      const user = await userApi.getUserByEmail(email);
      dispatch({ type: USER_ACTIONS.SET_SELECTED_USER, payload: user });
      return user;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.FETCH_USER;
      dispatch({ type: USER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Get user permissions
  const getUserPermissions = useCallback(async email => {
    dispatch({ type: USER_ACTIONS.SET_LOADING, payload: true });
    try {
      const permissions = await userApi.getUserPermissions(email);
      return permissions;
    } catch (error) {
      const errorMessage = error.response?.data?.["error-message"] || USER_ERROR_MESSAGES.FETCH_PERMISSIONS;
      dispatch({ type: USER_ACTIONS.SET_ERROR, payload: errorMessage });
      throw error;
    }
  }, []);

  // Select user
  const selectUser = useCallback(user => {
    dispatch({ type: USER_ACTIONS.SET_SELECTED_USER, payload: user });
  }, []);

  // Reset state
  const resetState = useCallback(() => {
    dispatch({ type: USER_ACTIONS.RESET_STATE });
  }, []);

  return {
    // State
    users: state.users,
    loading: state.loading,
    error: state.error,
    selectedUser: state.selectedUser,

    // Actions
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    getUserByEmail,
    getUserPermissions,
    selectUser,
    clearError,
    resetState,
  };
};
